/**
 * useAnalytics Hook
 *
 * React hook for accessing analytics functionality in components.
 * Provides convenient access to the analytics service methods.
 */

import { useCallback } from 'react';

import analytics from '../analyticsService';
import {
  AnalyticsFeature,
  AnalyticsMode,
  AnalyticsParams,
  LoginType,
  LogoutCause,
  UserProperties,
} from '../types';

export interface UseAnalyticsReturn {
  // Core methods
  logEvent: (eventName: string, params?: AnalyticsParams) => void;
  logScreenView: (screenName: string, screenClass?: string, params?: AnalyticsParams) => void;
  setUserId: (userId: string | null) => void;
  setUserProperties: (properties: UserProperties) => void;
  setAnalyticsMode: (mode: AnalyticsMode) => Promise<void>;
  getAnalyticsMode: () => AnalyticsMode;

  // Authentication events
  logLogin: (loginType?: LoginType, region?: string, service?: string) => void;
  logLogout: (cause?: LogoutCause) => void;
  logForgotPassword: () => void;
  logTwoFactorAuth: (success: boolean) => void;
  logSessionExpired: () => void;

  // Study events
  logStudyCreated: (centerId?: string) => void;
  logStudyViewed: (studyId: string) => void;
  logStudyUpdated: (studyId: string) => void;
  logStudyDeleted: (studyIds: string | string[]) => void;
  logStudySettingsUpdated: (studyId: string) => void;
  logStudyExtended: (studyId: string, nights: number) => void;
  logRawDataReviewed: (studyId: string) => void;
  logRawDataEdited: (studyId: string) => void;
  logStudyArchived: (studyIds: string | string[]) => void;
  logStudyRestored: (studyIds: string | string[]) => void;
  logReportGenerated: (reportType: string, studyId?: string) => void;
  logReportDownloaded: (reportType: string, studyId?: string) => void;
  logReportPrinted: (reportType: string, studyId?: string) => void;
  logReportGenerationFailed: (reportType: string, errorMessage?: string) => void;

  // Center events
  logCenterCreated: () => void;
  logCenterViewed: (centerId: string) => void;
  logCenterUpdated: (centerId: string) => void;
  logCenterSettingsUpdated: (centerId: string) => void;
  logCenterUserAdded: (centerId: string, userRole: string) => void;
  logCenterUserRemoved: (centerId: string) => void;
  logCenterUserDeactivated: (centerId: string) => void;
  logCenterUserRoleUpdated: (centerId: string, userRole: string) => void;
  logCenterSwitched: (centerId: string) => void;
  logSnippetCreated: (centerId: string) => void;
  logSnippetUpdated: (centerId: string) => void;
  logSnippetDeleted: (centerId: string) => void;

  // Profile events
  logProfileViewed: () => void;
  logProfileUpdated: () => void;
  logPasswordChanged: () => void;
  logSecuritySettingsUpdated: () => void;

  // Data viewer events
  logDataViewerAccessed: (studyId: string) => void;
  logNightViewed: (studyId: string, nightNumber: number) => void;
  logAllNightsViewed: (studyId: string) => void;
  logInterpretationViewed: (studyId: string) => void;
  logInterpretationSaved: (studyId: string) => void;
  logInterpretationSigned: (studyId: string) => void;
  logInterpretationReset: (studyId: string) => void;

  // Navigation events
  logNavigation: (destination: string) => void;

  // Error events
  logError: (errorType: string, errorMessage?: string, feature?: AnalyticsFeature) => void;

  // API Client events
  logApiClientUsed: (endpoint: string) => void;

  // Notes & Files events
  logNoteCreated: (studyId: string) => void;
  logInstructionsDownloaded: (studyId?: string) => void;

  // View events
  logSurveyViewed: (studyId: string) => void;
  logEventLogViewed: (studyId: string) => void;
  logStudyFiltered: () => void;
  logTermsOfUseViewed: () => void;
  logPrivacyPolicyViewed: () => void;
  logServiceSelectionShown: (serviceCount: number) => void;
  logRegionSelectionShown: (regionCount: number) => void;
}

/**
 * Hook for accessing analytics service methods in React components.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { logEvent, logLogin } = useAnalytics();
 *
 *   const handleLogin = () => {
 *     // ... login logic
 *     logLogin(LoginType.PASSWORD);
 *   };
 *
 *   return <button onClick={handleLogin}>Login</button>;
 * }
 * ```
 */
export function useAnalytics(): UseAnalyticsReturn {
  // Core methods
  const logEvent = useCallback((eventName: string, params?: AnalyticsParams) => {
    analytics.logEvent(eventName, params);
  }, []);

  const logScreenView = useCallback(
    (screenName: string, screenClass?: string, params?: AnalyticsParams) => {
      analytics.logScreenView({
        screenName,
        screenClass,
        params,
      });
    },
    []
  );

  const setUserId = useCallback((userId: string | null) => {
    analytics.setUserId(userId);
  }, []);

  const setUserProperties = useCallback((properties: UserProperties) => {
    analytics.setUserProperties(properties);
  }, []);

  const setAnalyticsMode = useCallback((mode: AnalyticsMode) => {
    return analytics.setAnalyticsMode(mode);
  }, []);

  const getAnalyticsMode = useCallback(() => {
    return analytics.getAnalyticsMode();
  }, []);

  // Authentication events
  const logLogin = useCallback((loginType?: LoginType, region?: string, service?: string) => {
    analytics.logLogin(loginType, region, service);
  }, []);

  const logLogout = useCallback((cause?: LogoutCause) => {
    analytics.logLogout(cause);
  }, []);

  const logForgotPassword = useCallback(() => {
    analytics.logForgotPassword();
  }, []);

  const logTwoFactorAuth = useCallback((success: boolean) => {
    analytics.logTwoFactorAuth(success);
  }, []);

  const logSessionExpired = useCallback(() => {
    analytics.logSessionExpired();
  }, []);

  // Study events
  const logStudyCreated = useCallback((centerId?: string) => {
    analytics.logStudyCreated(centerId);
  }, []);

  const logStudyViewed = useCallback((studyId: string) => {
    analytics.logStudyViewed(studyId);
  }, []);

  const logStudyUpdated = useCallback((studyId: string) => {
    analytics.logStudyUpdated(studyId);
  }, []);

  const logStudyDeleted = useCallback((studyIds: string | string[]) => {
    analytics.logStudyDeleted(studyIds);
  }, []);

  const logStudySettingsUpdated = useCallback((studyId: string) => {
    analytics.logStudySettingsUpdated(studyId);
  }, []);

  const logStudyExtended = useCallback((studyId: string, nights: number) => {
    analytics.logStudyExtended(studyId, nights);
  }, []);

  const logRawDataReviewed = useCallback((studyId: string) => {
    analytics.logRawDataReviewed(studyId);
  }, []);

  const logRawDataEdited = useCallback((studyId: string) => {
    analytics.logRawDataEdited(studyId);
  }, []);

  const logStudyArchived = useCallback((studyIds: string | string[]) => {
    analytics.logStudyArchived(studyIds);
  }, []);

  const logStudyRestored = useCallback((studyIds: string | string[]) => {
    analytics.logStudyRestored(studyIds);
  }, []);

  const logReportGenerated = useCallback((reportType: string, studyId?: string) => {
    analytics.logReportGenerated(reportType, studyId);
  }, []);

  const logReportDownloaded = useCallback((reportType: string, studyId?: string) => {
    analytics.logReportDownloaded(reportType, studyId);
  }, []);

  const logReportPrinted = useCallback((reportType: string, studyId?: string) => {
    analytics.logReportPrinted(reportType, studyId);
  }, []);

  const logReportGenerationFailed = useCallback((reportType: string, errorMessage?: string) => {
    analytics.logReportGenerationFailed(reportType, errorMessage);
  }, []);

  // Center events
  const logCenterCreated = useCallback(() => {
    analytics.logCenterCreated();
  }, []);

  const logCenterViewed = useCallback((centerId: string) => {
    analytics.logCenterViewed(centerId);
  }, []);

  const logCenterUpdated = useCallback((centerId: string) => {
    analytics.logCenterUpdated(centerId);
  }, []);

  const logCenterSettingsUpdated = useCallback((centerId: string) => {
    analytics.logCenterSettingsUpdated(centerId);
  }, []);

  const logCenterUserAdded = useCallback((centerId: string, userRole: string) => {
    analytics.logCenterUserAdded(centerId, userRole);
  }, []);

  const logCenterUserRemoved = useCallback((centerId: string) => {
    analytics.logCenterUserRemoved(centerId);
  }, []);

  const logCenterUserDeactivated = useCallback((centerId: string) => {
    analytics.logCenterUserDeactivated(centerId);
  }, []);

  const logCenterUserRoleUpdated = useCallback((centerId: string, userRole: string) => {
    analytics.logCenterUserRoleUpdated(centerId, userRole);
  }, []);

  const logCenterSwitched = useCallback((centerId: string) => {
    analytics.logCenterSwitched(centerId);
  }, []);

  const logSnippetCreated = useCallback((centerId: string) => {
    analytics.logSnippetCreated(centerId);
  }, []);

  const logSnippetUpdated = useCallback((centerId: string) => {
    analytics.logSnippetUpdated(centerId);
  }, []);

  const logSnippetDeleted = useCallback((centerId: string) => {
    analytics.logSnippetDeleted(centerId);
  }, []);

  // Profile events
  const logProfileViewed = useCallback(() => {
    analytics.logProfileViewed();
  }, []);

  const logProfileUpdated = useCallback(() => {
    analytics.logProfileUpdated();
  }, []);

  const logPasswordChanged = useCallback(() => {
    analytics.logPasswordChanged();
  }, []);

  const logSecuritySettingsUpdated = useCallback(() => {
    analytics.logSecuritySettingsUpdated();
  }, []);

  // Data viewer events
  const logDataViewerAccessed = useCallback((studyId: string) => {
    analytics.logDataViewerAccessed(studyId);
  }, []);

  const logNightViewed = useCallback((studyId: string, nightNumber: number) => {
    analytics.logNightViewed(studyId, nightNumber);
  }, []);

  const logAllNightsViewed = useCallback((studyId: string) => {
    analytics.logAllNightsViewed(studyId);
  }, []);

  const logInterpretationViewed = useCallback((studyId: string) => {
    analytics.logInterpretationViewed(studyId);
  }, []);

  const logInterpretationSaved = useCallback((studyId: string) => {
    analytics.logInterpretationSaved(studyId);
  }, []);

  const logInterpretationSigned = useCallback((studyId: string) => {
    analytics.logInterpretationSigned(studyId);
  }, []);

  const logInterpretationReset = useCallback((studyId: string) => {
    analytics.logInterpretationReset(studyId);
  }, []);

  // Navigation events
  const logNavigation = useCallback((destination: string) => {
    analytics.logNavigation(destination);
  }, []);

  // Error events
  const logError = useCallback(
    (errorType: string, errorMessage?: string, feature?: AnalyticsFeature) => {
      analytics.logError(errorType, errorMessage, feature);
    },
    []
  );

  // API Client events
  const logApiClientUsed = useCallback((endpoint: string) => {
    analytics.logApiClientUsed(endpoint);
  }, []);

  // Notes & Files events
  const logNoteCreated = useCallback((studyId: string) => {
    analytics.logNoteCreated(studyId);
  }, []);

  const logInstructionsDownloaded = useCallback((studyId?: string) => {
    analytics.logInstructionsDownloaded(studyId);
  }, []);

  // View events
  const logSurveyViewed = useCallback((studyId: string) => {
    analytics.logSurveyViewed(studyId);
  }, []);

  const logEventLogViewed = useCallback((studyId: string) => {
    analytics.logEventLogViewed(studyId);
  }, []);

  const logStudyFiltered = useCallback(() => {
    analytics.logStudyFiltered();
  }, []);

  const logTermsOfUseViewed = useCallback(() => {
    analytics.logTermsOfUseViewed();
  }, []);

  const logPrivacyPolicyViewed = useCallback(() => {
    analytics.logPrivacyPolicyViewed();
  }, []);

  const logServiceSelectionShown = useCallback((serviceCount: number) => {
    analytics.logServiceSelectionShown(serviceCount);
  }, []);

  const logRegionSelectionShown = useCallback((regionCount: number) => {
    analytics.logRegionSelectionShown(regionCount);
  }, []);

  return {
    // Core methods
    logEvent,
    logScreenView,
    setUserId,
    setUserProperties,
    setAnalyticsMode,
    getAnalyticsMode,

    // Authentication events
    logLogin,
    logLogout,
    logForgotPassword,
    logTwoFactorAuth,
    logSessionExpired,

    // Study events
    logStudyCreated,
    logStudyViewed,
    logStudyUpdated,
    logStudyDeleted,
    logStudySettingsUpdated,
    logStudyExtended,
    logRawDataReviewed,
    logRawDataEdited,
    logStudyArchived,
    logStudyRestored,
    logReportGenerated,
    logReportDownloaded,
    logReportPrinted,
    logReportGenerationFailed,

    // Center events
    logCenterCreated,
    logCenterViewed,
    logCenterUpdated,
    logCenterSettingsUpdated,
    logCenterUserAdded,
    logCenterUserRemoved,
    logCenterUserDeactivated,
    logCenterUserRoleUpdated,
    logCenterSwitched,
    logSnippetCreated,
    logSnippetUpdated,
    logSnippetDeleted,

    // Profile events
    logProfileViewed,
    logProfileUpdated,
    logPasswordChanged,
    logSecuritySettingsUpdated,

    // Data viewer events
    logDataViewerAccessed,
    logNightViewed,
    logAllNightsViewed,
    logInterpretationViewed,
    logInterpretationSaved,
    logInterpretationSigned,
    logInterpretationReset,

    // Navigation events
    logNavigation,

    // Error events
    logError,

    // API Client events
    logApiClientUsed,

    // Notes & Files events
    logNoteCreated,
    logInstructionsDownloaded,

    // View events
    logSurveyViewed,
    logEventLogViewed,
    logStudyFiltered,
    logTermsOfUseViewed,
    logPrivacyPolicyViewed,
    logServiceSelectionShown,
    logRegionSelectionShown,
  };
}

export default useAnalytics;
